import { atom } from 'recoil';
import { DataType } from 'enums';
import { removeLeadingZeros } from 'functions';
import { DateFilterState, DefaultDateFilter, useDateFilter } from 'state';
import { DisputeSummary, InvoiceSummary, PaymentDetail, PaymentSummary, SearchResult } from 'types';

type Sort = {
    text: string,
    order: 'asc' | 'desc',
    compare: (a: SearchResult, b: SearchResult, searchvalue?: string) => number
};

export const getAdjustmentSearchAmount = ({ adjustmentAmount }: PaymentDetail) => adjustmentAmount;

export const getAdjustmentFilterDate = ({ invoiceDate, paymentDate, scheduledPayDate }: PaymentDetail) =>
    paymentDate ?? invoiceDate ?? scheduledPayDate;

export const getAdjustmentSortDate = ({ paymentDate, scheduledPayDate, invoiceDate }: PaymentDetail) =>
    paymentDate ?? scheduledPayDate ?? invoiceDate;

export const getSearchResultAmount = (item: SearchResult): number | undefined => {
    switch (item.type) {
        case DataType.Dispute: return (item.data as DisputeSummary).disputeAmount;
        case DataType.Invoice: return (item.data as InvoiceSummary).userVoucherDecimal;
        case DataType.Payment: return (item.data as PaymentSummary).paymentAmount;
        case DataType.CustomerDeduction:
        case DataType.DotBillback:
        case DataType.CustomerDedRepay:
        case DataType.Credit:
        case DataType.Unknown:
            return getAdjustmentSearchAmount(item.data as PaymentDetail);
    }
};

export const getSearchResultDate = (item: SearchResult): Date | undefined => {
    switch (item.type) {
        case DataType.Dispute: return (item.data as DisputeSummary).submittedDateUtc;
        case DataType.Invoice: return (item.data as InvoiceSummary).invoiceDate;
        case DataType.Payment: return (item.data as PaymentSummary).paymentDate;
        case DataType.CustomerDeduction:
        case DataType.DotBillback:
        case DataType.CustomerDedRepay:
        case DataType.Credit:
        case DataType.Unknown:
            return getAdjustmentSortDate(item.data as PaymentDetail);
    }
};

const getSearchResultDateTime = (item: SearchResult, missingDateValue = 0) =>
    getSearchResultDate(item)?.getTime() ?? missingDateValue;

export const SearchResultsSortOptions: Sort[] = [
    {
        text: 'Sort By',
        order: 'asc',
        compare: (a: SearchResult, b: SearchResult, searchvalue: string|undefined) => {
            if (a.type === DataType.Dispute) {
                const exactMatchA = 'disputeNumber' in a.data && removeLeadingZeros(a.data.disputeNumber) == removeLeadingZeros(searchvalue) ? 0 : 1;
                const exactMatchB = 'disputeNumber' in b.data && removeLeadingZeros(b.data.disputeNumber) == removeLeadingZeros(searchvalue) && b.type == a.type ? 0 : 1;
                return exactMatchA - exactMatchB;

            }
            if (a.type === DataType.Payment) {
                const exactMatchA = 'paymentIdRef' in a.data && removeLeadingZeros(a.data.paymentIdRef ?? "") == removeLeadingZeros(searchvalue) ? 0 : 1;
                const exactMatchB = 'paymentIdRef' in b.data && removeLeadingZeros(b.data.paymentIdRef ?? "") == removeLeadingZeros(searchvalue) && b.type == a.type  ? 0 : 1;
                return exactMatchA - exactMatchB;
            }
            if (a.type === DataType.Invoice) {
                const exactMatchA = 'invoiceId' in a.data && removeLeadingZeros(a.data.invoiceId ?? "") == removeLeadingZeros(searchvalue) ? 0 : 1;
                const exactMatchB = 'invoiceId' in b.data && removeLeadingZeros(b.data.invoiceId ?? "") == removeLeadingZeros(searchvalue) && b.type == a.type  ? 0 : 1;
                return exactMatchA - exactMatchB;
            }
            return a.type - b.type;
        }
    },
    {
        text: 'Date: Ascending',
        order: 'asc',
        compare: (a: SearchResult, b: SearchResult) =>
            getSearchResultDateTime(a, Number.MAX_SAFE_INTEGER) -
            getSearchResultDateTime(b, Number.MAX_SAFE_INTEGER)
    },
    {
        text: 'Date: Descending',
        order: 'desc',
        compare: (a: SearchResult, b: SearchResult) => getSearchResultDateTime(b) - getSearchResultDateTime(a)
    },
    {
        text: 'Amount: High to Low',
        order: 'desc',
        compare: (a: SearchResult, b: SearchResult) => (getSearchResultAmount(b) ?? 0) - (getSearchResultAmount(a) ?? 0)
    },
    {
        text: 'Amount: Low to High',
        order: 'asc',
        compare: (a: SearchResult, b: SearchResult) => (getSearchResultAmount(a) ?? 0) - (getSearchResultAmount(b) ?? 0)
    }
];

type SearchResultsFilterState = {
    dataType?: DataType,
    startDate?: Date,
    endDate?: Date,
    startAmount?: number,
    endAmount?: number,
    sort: Sort,
} & DateFilterState;

export const searchResultsFilter = atom<SearchResultsFilterState>({
    key: 'searchResultsFilter',
    default: {
        ...DefaultDateFilter,
        sort: SearchResultsSortOptions[0]
    }
});

export const useSearchResultsFilter = () => {
    const { setField, ...state } = useDateFilter(searchResultsFilter);
    
    return {
        ...state,
        setDataType: (value?: number) => setField('dataType', value),
        setStartAmount: (value?: number) => setField('startAmount', value),
        setEndAmount: (value?: number) => setField('endAmount', value),
        setSort: (value: Sort) => setField('sort', value),
    };
};
